import { useRef, useCallback, ChangeEvent } from 'react';

import { Input, RadioInput, Select } from '@new-components/elements';
import { useFormContext } from '@hooks/react-hook-form';
import {
  ApplicationDataResponse,
  useRGetApplicationData,
} from '@repositories/new-application';

import ApplicationCard from '../../../../../application-card';
import ApplicationAlert from '../../../../../application-alert';
import SectionTitle from '../../../../../section-title';
import { PersonalDataProps } from './types';

import { email as emailValidator } from '@utils/validator';

const PersonalData = (props: PersonalDataProps) => {
  const { readonly } = props;
  const {
    fieldsRef,
    setError,
    clearErrors,
    formState: { isSubmitSuccessful, isSubmitting },
  } = useFormContext();

  const emailInputRef = useRef<HTMLInputElement>(null);

  const {
    data,
    isLoading: isLoadingGetApplicationData,
    isError: isErrorGetApplicationData,
    refetch: refetchGetApplicationData,
  } = useRGetApplicationData<ApplicationDataResponse<1>>({
    page: 1,
  });

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!emailValidator(value)) {
        setError('email', {
          message: 'Mohon masukkan alamat email yang valid',
        });
      } else {
        clearErrors('email');
      }
      e.target.removeAttribute('data-available-email');
    },
    [clearErrors, setError],
  );

  return (
    <ApplicationCard className='section-personal-data'>
      <SectionTitle title='Data Pribadi' />

      {!readonly && (
        <ApplicationAlert
          marginBottom='1.25rem'
          message='Pastikan informasi yang Anda masukkan sesuai dengan informasi yang tertera pada KTP Anda.'
        />
      )}

      <Input
        ref={(e) => (fieldsRef.current['nik'] = e)}
        name='nik'
        label='NIK'
        disabled
      />
      <Input
        ref={(e) => (fieldsRef.current['email'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='email'
        label='Email'
        placeholder='email@example.com'
        onChange={handleEmailChange}
        inputRef={emailInputRef}
      />
      <Input
        ref={(e) => (fieldsRef.current['fullname'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='fullname'
        label='Nama Lengkap'
        placeholder='nama lengkap'
      />
      <Input
        ref={(e) => (fieldsRef.current['dob'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        type='date'
        name='dob'
        label='Tanggal Lahir'
      />
      <RadioInput
        ref={(e) => (fieldsRef.current['gender'] = e)}
        name='gender'
        label='Jenis Kelamin'
        flexOrientation='row'
        radioButtonProps={{ color: 'primary' }}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        options={[
          { label: 'Pria', value: 'Pria' },
          { label: 'Wanita', value: 'Wanita' },
        ]}
      />
      <Select
        ref={(e) => (fieldsRef.current['lastEducation'] = e)}
        name='lastEducation'
        label='Pendidikan Terakhir'
        placeholder='pilih'
        disabled={
          readonly ||
          isLoadingGetApplicationData ||
          (isSubmitting && !isSubmitSuccessful)
        }
        onFocus={() => isErrorGetApplicationData && refetchGetApplicationData()}
        options={data?.data.lastEducations}
      />
    </ApplicationCard>
  );
};

export default PersonalData;
